import type { Component } from 'vue'
import { h, TransitionGroup } from 'vue'

import { clone, debounce, isFunction, mapKeys } from 'lodash';

import { isMobileDevice, ucFirstFilter } from '@wittignl/js-core'

import '../../../scss/component/overlay/Overlay.scss'
import type { OverlayPlugin } from '../../plugin';

const CLOSE_VALID_TIMEOUT = 200;

interface DownInfo {
    element: Element
    date: Date
}

export const Overlay: Component = {

    props: {
        'zStart': {
            type: Number,
            default: 1000
        },
        'transition': {
            type: String,
            default: 'fade'
        }
    },

    data(){

        return {
            visible: false,
            listenersAdded: false
        }
    },

    created(){

        if(!this.$overlay){
            throw 'Overlay plugin not initialized';
        }
    },

    mounted(){

        this.updateListenersDebounced = debounce(() => this.updateListeners(), 100);
        this.boundDocumentClick = this.onDocumentClick.bind(this);
        this.boundDocumentMouseDown = this.onDocumentMouseDown.bind(this);

        this.$watch('$overlay.overlays.length', (length) => {

            if(length > 0){
                this.visible = true;
            }
        })
    },

    updated(){

        this.updateListenersDebounced();
    },

    render(){

        const overlayPlugin: OverlayPlugin = this.$overlay;

        const rootOptions = {
            class: 'ui-overlay-container',
            name: this.transition,
            tag: 'div',
            style: {
                display: this.visible ? 'block' : 'none'
            },
            onAfterLeave: this.afterTransitionLeave
        };

        let zCounter = parseInt(this.zStart);
        let firstShaderZ: number = null;

        const overlays = [];

        for(let overlay of overlayPlugin.overlays){

            const options = overlay.options;

            const overlayElement: any = options?.layer || 'div';
            const overlayChildren = options?.children || [];

            const layerRef = `layer_${overlay.id}`;
            const overlayRef = `overlay_${overlay.id}`;

            const layerOptions = {
                key: overlay.id,
                ref: layerRef,
                options,
                style: {
                    'z-index': zCounter,
                    'pointer-events': options.blocking ? 'auto' : 'none'
                },
                class: {
                    'no-animate': options.animate === false
                }
            };

            const $layer = h(overlayElement, layerOptions, {

                default: slotProps => {

                    const baseProps = isFunction(overlay.props) ? overlay.props() : overlay.props;

                    const overlayOptions = Object.assign({

                        ref: overlayRef,
                        onDismiss: (data) => overlayPlugin.dismiss(overlay, data),
                        onResolve: (data) => overlayPlugin.resolve(overlay, data),

                        ...mapKeys(options.on || {}, (_, onKey) => 'on' + ucFirstFilter(onKey))

                    }, baseProps, slotProps);

                    const $children = overlayChildren.map(child => h(child, clone(overlayOptions), options.slots || {}));

                    return h(overlay.component, clone(overlayOptions), { default: () => $children, ...(options.slots || {}) });
                }
            });

            if(options.shader && firstShaderZ === null){
                firstShaderZ = zCounter;
            }

            overlay.$overlayRef = () => this.$refs[overlayRef];
            overlay.$layerRef = () => this.$refs[layerRef];

            zCounter += 2;

            overlays.push($layer);
        }

        if(firstShaderZ !== null){

            overlays.unshift(h('div', {
                key: 'shader',
                class: 'shader',
                style: {
                    'z-index': firstShaderZ - 1
                }
            }));
        }

        return h(TransitionGroup, rootOptions, { default: () => overlays });
    },

    methods: {

        updateListeners(){

            const overlayPlugin: OverlayPlugin = this.$overlay;

            if(overlayPlugin.overlays.length > 0){

                if(!this.listenersAdded){
                    this.attachListeners();
                }
            }
            else {

                if(this.listenersAdded){
                    this.removeListeners();
                }
            }
        },

        attachListeners(){

            // Hack for mobile device to enable click events
            if(isMobileDevice()){
                document.body.style.cursor = 'pointer';
            }

            document.addEventListener('mousedown', this.boundDocumentMouseDown);
            document.addEventListener('click', this.boundDocumentClick);

            this.listenersAdded = true;
        },

        removeListeners(){

            if(isMobileDevice()){
                document.body.style.cursor = null;
            }

            document.removeEventListener('mousedown', this.boundDocumentMouseDown);
            document.removeEventListener('click', this.boundDocumentClick);

            this.listenersAdded = false;
        },

        afterTransitionLeave(){

            const overlayPlugin: OverlayPlugin = this.$overlay;

            if(overlayPlugin.overlays.length === 0){
                this.visible = false;
            }
        },

        onDocumentMouseDown(e: Event){

            this.downInfo = {
                element: e.target,
                date: new Date()
            };
        },

        onDocumentClick(e: Event){

            const overlayPlugin: OverlayPlugin = this.$overlay;
            const target = e.target as Element;

            const topOverlay = overlayPlugin.topOverlay;
            if(!topOverlay || !target){
                return;
            }

            const element = topOverlay?.$overlayRef()?.$el as Element
            if(!element){
                return;
            }

            const downInfo: DownInfo = this.downInfo;

            if(downInfo && (element.contains(downInfo.element) || downInfo.date.getTime() - topOverlay.showDate.getTime() < CLOSE_VALID_TIMEOUT)){
                return;
            }

            // Only cancel when target is still rendered
            if(document.body.contains(target) && !element.contains(target)){
                overlayPlugin.cancelTopOverlay(e);
            }
        }
    }
};

export default Overlay;